import { emptyInput, type PlayerInput, type SimEvent, type SimState, type WorldQuery } from '../core/sim.ts';
import { SNAPSHOT_HZ, TICK_DT, type ClassId, type Faction } from '../core/constants.ts';
import { Prediction } from './prediction.ts';
import type { ChatMessage, ChatScope } from '../core/chat.ts';
import type { Transport } from './transport.ts';
import type { GameRoom } from './room.ts';

/**
 * A client's end of a room.
 *
 * The other half of the split. `LocalTransport` was a simulation wearing a
 * transport's clothes: it owned the state, stepped it, drove the bots and
 * answered as though it had come off a wire. Nothing about it could hold two
 * players, because the object holding the world also assumed it *was* the
 * player.
 *
 * This holds no world at all. It sends input one way and receives a view the
 * other, which is the only thing a client ever does — and it means the pipe in
 * between can be a function call in the same process today and a socket
 * tomorrow without either end noticing.
 *
 * Two of these against one room is two players on one machine, which is the
 * cheapest possible proof that the loop closes.
 */

/**
 * How a client reaches its room.
 *
 * Deliberately tiny. Everything a client needs to do is send an input, send a
 * line of chat, and be given back what it may see — and a surface this small is
 * a surface a socket can implement without argument.
 */
export interface RoomLink {
  readonly playerId: string;
  /**
   * One input for one tick. When it carries a `seq`, that is the number the
   * room must acknowledge it by; a link that numbers inputs itself must use it.
   */
  send(input: PlayerInput & { seq?: number }): void;
  say(scope: ChatScope, text: string): void;
  /** Lobby only. Absent on a link that has no room behind it. */
  setReady?(on: boolean): void;
  reseat?(faction: Faction, cls: ClassId, loadout?: string[]): void;
  addBot?(faction: Faction): void;
  rename?(name: string): void;
  /** Everything since the last call: the world, what happened, what was said. */
  collect(): {
    state: SimState;
    events: SimEvent[];
    chat: ChatMessage[];
    alpha: number;
    /**
     * The last input this room had processed, or absent when there is no room to
     * be behind — a loopback answers immediately and has nothing to acknowledge.
     */
    ack?: number;
  };
  /** Drives the room, where the client is also the host. Absent over a socket. */
  advance?(elapsed: number): void;
  /** Turns relevancy filtering on. Absent over a socket, where it is not a choice. */
  filter?(on: boolean): void;
}

/**
 * A link to a room in this same process.
 *
 * No serialisation, no latency, no reordering — which makes it a poor rehearsal
 * for a network and an excellent way to find out whether the *shape* is right.
 * The things it cannot catch are exactly the things a socket is for.
 */
export function loopback(room: GameRoom, playerId: string, host = false): RoomLink {
  /*
   * Unfiltered by default, which is the opposite of what a socket does.
   *
   * Over a wire the filter is a security measure and is never optional. In this
   * same process there is nothing to protect against — the client could read the
   * room's own memory if it wanted to — so leaving it off keeps single-player
   * behaving exactly as it always has, and `filter` turns it on for anybody who
   * wants to see what the rule feels like before there is a server.
   */
  let filtered = false;
  return {
    playerId,
    send: (input) => room.input(playerId, input),
    say: (scope, text) => { room.speak(playerId, scope, text); },
    collect: () => ({ ...room.collect(playerId, filtered), alpha: room.alpha }),
    advance: host ? (elapsed) => room.advance(elapsed) : undefined,
    setReady: (on) => { room.setReady(playerId, on); },
    addBot: (faction) => { room.addBotTo(faction); },
    rename: (name) => { room.rename(playerId, name); },
    reseat: (faction, cls, loadout) => { room.reseat(playerId, faction, cls, loadout); },
    filter: (on: boolean) => { filtered = on; },
  };
}

export class RoomTransport implements Transport {
  readonly localPlayerId: string;

  private readonly link: RoomLink;
  private readonly onEvents?: (events: SimEvent[]) => void;
  private readonly onChat?: (msg: ChatMessage) => void;

  private latest: SimState;
  private running = false;
  /**
   * Whether the round is actually running, from the last snapshot.
   *
   * Prediction is switched off when it is not: a freeze or an intermission means
   * the server is holding everybody still, and a client that keeps walking into
   * a correction sixty times a second shakes the camera apart.
   */
  private live = true;

  /** Null when there is no wire to be ahead of. See `predictWith`. */
  private predict: Prediction | null = null;
  private world: WorldQuery | null = null;

  constructor(
    link: RoomLink,
    seed: SimState,
    hooks: {
      onEvents?: (events: SimEvent[]) => void;
      onChat?: (msg: ChatMessage) => void;
    } = {},
  ) {
    this.link = link;
    this.localPlayerId = link.playerId;
    this.latest = seed;
    this.onEvents = hooks.onEvents;
    this.onChat = hooks.onChat;
  }

  /**
   * The last view the room handed over.
   *
   * A *copy* of the room's truth in the general case — filtered, and one tick
   * behind by the time it is drawn. The renderer has never needed more than
   * that, which is why none of it has to change.
   */
  getState(): SimState {
    return this.latest;
  }

  /** Says whether this player is ready. Ignored outside the lobby. */
  setReady(on: boolean): void {
    this.link.setReady?.(on);
  }

  /** Changes side or discipline. Ignored outside the lobby. */
  reseat(faction: Faction, cls: ClassId, loadout?: string[]): void {
    this.link.reseat?.(faction, cls, loadout);
  }

  /**
   * Records what the keys are saying. Acted on by `advance`, at the tick rate.
   *
   * This used to predict and send immediately, once per *frame*, stepping a
   * whole `TICK_DT` each time — so prediction advanced at the frame rate rather
   * than the simulation's. At two hundred and forty frames a second that is four
   * ticks of movement a frame against the server's one, and every snapshot
   * hauled the mage back to where it should have been. Moving forward and being
   * pulled back sixty times a second is not read as "too fast"; it is read as
   * weight, and the faster the machine the heavier it felt.
   *
   * Simulated movement belongs on the simulation's clock. The frame rate decides
   * how often the world is *drawn* and should decide nothing else.
   */
  sendInput(input: PlayerInput): void {
    if (!this.running) return;
    this.wanted = input;
  }

  /** What the player is asking for, as of the last frame. */
  private wanted: PlayerInput | null = null;
  /** Real time banked toward the next fixed input tick. */
  private sendAccumulator = 0;

  /**
   * Predicts and sends on the simulation's clock, not the renderer's.
   *
   * Run locally first, then sent: without that a player presses a key and waits
   * for a round trip before anything moves — invisible on a local network,
   * treacle across an ocean, and not something interpolation can hide, because
   * interpolation smooths *other people* and the one thing it cannot smooth is
   * the gap between a key and your own feet.
   */
  private pump(elapsed: number): void {
    const input = this.wanted;
    if (!input) return;

    this.sendAccumulator += Math.min(0.25, elapsed);
    let steps = 0;
    while (this.sendAccumulator >= TICK_DT && steps < 4) {
      this.sendAccumulator -= TICK_DT;
      steps += 1;

      /*
       * Every input sent is numbered by the prediction, live or not, and goes on
       * the wire with that number.
       *
       * The link used to number inputs itself, and the prediction numbered only
       * the ones it recorded — which was only while the round was live. So every
       * input sent in the lobby, the pre-round freeze or an intermission put the
       * two counts further apart, the room's acknowledgement named inputs the
       * prediction had never heard of, and each snapshot discarded the whole
       * unacknowledged queue instead of replaying it. The predicted mage was
       * dragged back to the server's position, a round trip stale, twenty times
       * a second: slow and choppy at any frame rate, and worse the longer you had
       * been connected.
       */
      if (this.predict) {
        const stamped = this.predict.record(input, TICK_DT);
        if (this.world && this.live) this.predict.step(input, this.world, TICK_DT);
        this.link.send(stamped);
      } else {
        this.link.send(input);
      }
    }
  }

  /**
   * Turns on prediction, against a world that can resolve a body.
   *
   * Given the client's own physics rather than building its own, so that the
   * capsule a prediction is resolved against is the very one the camera follows.
   */
  predictWith(world: WorldQuery): void {
    this.predict = new Prediction();
    this.world = world;
  }

  say(scope: ChatScope, text: string): void {
    this.link.say(scope, text);
  }

  /** Practice for the network's visibility rule, against a local match. */
  filterView(on: boolean): void {
    this.link.filter?.(on);
  }

  /** How far into the unsimulated tick to draw. See the interpolation work. */
  alpha = 1;

  /**
   * How far into the next tick to draw *your own* mage.
   *
   * In a local match that is the room's alpha, like everybody else's. Predicted,
   * it is this client's own tick clock — how far the send accumulator has got
   * towards the next predicted tick — because the predicted mage is stepped by
   * that clock and not the server's. The camera follows this; drawn at the
   * snapshot's alpha of 1 instead, it stepped sixty times a second however fast
   * the screen was.
   */
  get localAlpha(): number {
    if (this.predict && this.live) return Math.min(1, this.sendAccumulator / TICK_DT);
    return this.alpha;
  }

  /**
   * Drives the room if this client is hosting it, then collects.
   *
   * Over a socket the first half is gone — the server advances on its own clock
   * and this only ever collects. That the two cases differ by one optional
   * method is the point of the split.
   */
  advance(elapsed: number): void {
    if (!this.running) return;
    // Before the room is driven, so a hosted tick sees this frame's input rather
    // than the one before it.
    this.pump(elapsed);
    this.link.advance?.(elapsed);

    const view = this.link.collect();
    this.alpha = view.alpha;

    /*
     * Predicted, where there is a wire to be ahead of.
     *
     * The renderer draws whatever `getState` returns, so the predicted position
     * has to be *in* that state rather than beside it — a mage in two places is
     * a mage the nameplates, the sounds and the camera all disagree about.
     */
    /*
     * Predicted only while the round is actually running.
     *
     * `Prediction` calls `movePlayer` directly rather than going through `step`,
     * which is the whole point of it — replaying a dozen inputs through the full
     * simulation would be wasteful and wrong. But it also means it does not see
     * the phase gate that `step` applies, so during a pre-round freeze or an
     * intermission the client went on walking and falling while the server held
     * everybody still. Every snapshot then yanked the mage back, sixty times a
     * second, and the camera shook hard enough to be unplayable.
     *
     * Frozen, there is nothing to predict: the server is not moving anybody and
     * its state is exactly right. `forget` drops the unacknowledged queue so the
     * first live frame replays from where the server actually has us rather than
     * from a stack of inputs that were never simulated.
     */
    this.live = view.state.match?.phase === 'live';
    if (this.predict && view.ack !== undefined && this.live) {
      const mine = view.state.players.find((p) => p.id === this.localPlayerId);
      if (mine) this.predict.reconcile(mine, view.ack, this.world!);
      this.latest = this.leadProjectiles(
        this.predict.applyTo(view.state, this.localPlayerId), view.alpha,
      );
    } else {
      if (!this.live) this.predict?.forget();
      this.latest = view.state;
    }

    if (view.events.length && this.onEvents) this.onEvents(view.events);
    if (this.onChat) for (const msg of view.chat) this.onChat(msg);
  }

  start(): void {
    this.running = true;
  }

  /**
   * Spells in flight, drawn where they are *now* rather than where the last
   * snapshot found them.
   *
   * A snapshot is a photograph twenty times a second, so a projectile drawn
   * from it jumped twenty times a second. And your own spells were a round trip
   * late on top: you cast on your screen, the room spawned it when your input
   * arrived, and you saw it when the snapshot came back — by which time you
   * had moved on and it appeared to leave from behind you.
   *
   * Every projectile is carried forward along its line by the time since its
   * snapshot landed, so it moves smoothly; your own are carried a further round
   * trip — the inputs still in flight, which is exactly how far your predicted
   * mage is ahead of the room — so they leave from where you are. Each is
   * stopped short of any wall in the way, so a lead never draws a spell through
   * one; the room still decides what it hit, and its impact removes it.
   */
  private leadProjectiles(state: SimState, alpha: number): SimState {
    if (!state.projectiles.length || !this.world || !this.predict) return state;
    const since = Math.max(0, alpha) / SNAPSHOT_HZ;
    const lead = this.predict.inFlight * TICK_DT;
    const projectiles = state.projectiles.map((pr) => {
      const ahead = since + (pr.ownerId === this.localPlayerId ? lead : 0);
      const want = pr.speed * ahead;
      if (want <= 0) return pr;
      const hit = this.world!.raycast(pr.pos, pr.dir, want);
      const go = hit ? Math.max(0, hit.distance - 0.05) : want;
      return {
        ...pr,
        pos: { x: pr.pos.x + pr.dir.x * go, y: pr.pos.y + pr.dir.y * go, z: pr.pos.z + pr.dir.z * go },
      };
    });
    return { ...state, projectiles };
  }

  stop(): void {
    this.running = false;
  }
}

/** An input for a client that has gone quiet, so the room does not stall. */
export const NO_INPUT: PlayerInput = emptyInput();
